import {ChangeDetectionStrategy, Component, computed, inject, OnInit, signal} from '@angular/core';
import {NzDropdownModule} from "ng-zorro-antd/dropdown";
import {NzAvatarModule} from "ng-zorro-antd/avatar";
import {NzIconModule} from "ng-zorro-antd/icon";
import {Router} from "@angular/router";
import {NzModalModule, NzModalService} from "ng-zorro-antd/modal";
import {NzMessageService} from "ng-zorro-antd/message";
import {StorageService} from "../../../../services/storage.service";
import {AccountService} from "../../../../services/account.service";
import {AccountApiService} from "../../../../services/apis/account.api.service";
import {RegionService} from "../../../../services/region.service";
import {SupabaseAuthService} from "../../../../services/supabase-auth.service";
import {isSameSupabaseAuthEnv} from "../../../../supabase.env";
import {RegionCode} from "../../../../app.env";
import {environment} from "../../../../../environments/environment";
import {NzButtonModule} from "ng-zorro-antd/button";
import {I18nService} from "../../../../core/i18n/i18n.service";
import {AppLanguage} from "../../../../core/i18n/i18n.config";
import {TranslocoPipe, TranslocoService} from "@jsverse/transloco";
import {regionSwitchTarget} from './region-switch-navigation';
import {resolveAccountAvatarUrl} from "../../../../shared/lib/account-avatar-url";
import {resolveAccountOAuthConfig} from "../../../account/oauth-callback/oauth-callback.constants";

@Component({
    selector: 'app-header',
    imports: [
        NzDropdownModule,
        NzAvatarModule,
        NzIconModule,
        NzModalModule,
        NzButtonModule,
        TranslocoPipe,
    ],
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {

  readonly username = signal<string>('');
  readonly email = signal('');
  readonly avatarUrl = signal<string | null>(null);
  protected readonly region = signal<string>('');
  protected readonly debugMode = signal(false);
  protected readonly version = signal<string>('');
  protected readonly activeLanguage = inject(I18nService).activeLanguage;
  protected readonly activeLanguageLabel = inject(I18nService).activeLanguageLabel;
  protected readonly languageOptions = [
    {label: 'English', value: 'en-US'},
    {label: '简体中文', value: 'zh-CN'},
  ] as const;
  private readonly prodRegionOptions = [
    {value: RegionCode.US},
    {value: RegionCode.EU},
    {value: RegionCode.CN},
  ] as const;
  private readonly debugRegionOptions = [
    {value: RegionCode.DEV},
    {value: RegionCode.LOCAL},
    {value: RegionCode.LOCAL_PROD},
  ] as const;
  /** Debug regions are visible only when debug mode is enabled. */
  protected readonly visibleRegionOptions = computed(() =>
    this.debugMode()
      ? [...this.prodRegionOptions, ...this.debugRegionOptions]
      : [...this.prodRegionOptions],
  );
  protected readonly regionLabel = computed(() => {
    this.activeLanguage();
    return this.regionName(this.region() || RegionCode.US);
  });
  protected readonly userLabel = computed(() => this.username() || 'User');

  private readonly storageService = inject(StorageService);
  private readonly accountService = inject(AccountService);
  private readonly accountApiService = inject(AccountApiService);
  private readonly regionService = inject(RegionService);
  private readonly supabaseAuthService = inject(SupabaseAuthService);
  private readonly nzModalService = inject(NzModalService);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly router = inject(Router);
  private readonly i18nService = inject(I18nService);
  private readonly translocoService = inject(TranslocoService);

  async ngOnInit() {
    this.region.set(this.regionService.regionCode() || 'US');
    this.debugMode.set(await this.regionService.isDebugMode());
    const userInfo = await this.storageService.lfGet('userInfo') as { username?: string } | null;
    this.username.set(userInfo?.username ?? '');
    this.version.set(environment.version);

    try {
      const [user, identities] = await Promise.all([
        this.supabaseAuthService.getUser(),
        this.supabaseAuthService.getUserIdentities(),
      ]);
      this.email.set(user.email?.trim() ?? '');
      this.avatarUrl.set(resolveAccountAvatarUrl(user, identities) ?? null);
    } catch {
      // ignore — avatar/email are cosmetic in the header
    }
  }

  protected readonly accountProfileUrl = computed(() => {
    const cfg = resolveAccountOAuthConfig(this.region() || RegionCode.US);
    return `${cfg.accountAppUrl.replace(/\/$/, '')}/profile`;
  });

  async onOpenAccountInfo() {
    window.open(this.accountProfileUrl(), '_blank', 'noopener,noreferrer');
  }

  async onLogout() {
    this.nzModalService.confirm({
      nzTitle: this.translocoService.translate('shell.header.logout_confirm_title'),
      nzOnOk: async () => {
        await this.logout();
      }
    });
  }

  async onSwitchLanguage(language: AppLanguage) {
    await this.i18nService.setLanguage(language);
  }

  async onRegionMenuVisible(visible: boolean) {
    if (!visible) {
      return;
    }
    this.debugMode.set(await this.regionService.isDebugMode());
  }

  async onSwitchRegion(regionCode: string) {
    const fromRegion = this.region();
    if (!regionCode || regionCode === fromRegion) {
      return;
    }

    const isDebugRegion = this.debugRegionOptions.some((option) => option.value === regionCode);
    if (isDebugRegion && !(await this.regionService.isDebugMode())) {
      return;
    }

    // US/EU/CN/LOCAL_PROD share mp-prod; DEV/LOCAL share mp-dev — token stays valid.
    if (isSameSupabaseAuthEnv(fromRegion, regionCode)) {
      await this.switchRegionKeepSession(regionCode);
      return;
    }

    const targetName = this.regionName(regionCode);
    this.nzModalService.confirm({
      nzTitle: this.translocoService.translate('shell.header.switch_region_confirm_title', {
        region: targetName,
      }),
      nzContent: this.translocoService.translate('shell.header.switch_region_confirm_content'),
      nzOnOk: async () => {
        const loading = this.nzMessageService.loading(
          this.translocoService.translate('shell.header.switch_region_switching', {
            region: targetName,
          }),
          {nzDuration: 0},
        );
        try {
          // Persist target Auth region *before* logout — logout navigates away and
          // prepareAccountAppOAuth must see US/EU/CN to hit prod mp-auth + Auth.
          await this.regionService.setRegion(regionCode);
          this.supabaseAuthService.resetClient();
          this.region.set(this.regionService.regionCode());
          await this.accountService.logout();
        } finally {
          this.nzMessageService.remove(loading.messageId);
        }
      },
    });
  }

  private async switchRegionKeepSession(regionCode: string) {
    const fromRegion = this.region();
    const currentUrl = this.router.url;
    const targetName = this.regionName(regionCode);
    const loading = this.nzMessageService.loading(
      this.translocoService.translate('shell.header.switch_region_switching', {
        region: targetName,
      }),
      {nzDuration: 0},
    );

    try {
      await this.regionService.setRegion(regionCode);
      this.region.set(this.regionService.regionCode());

      const uid = await this.storageService.lfGet('uid') as string | null;
      if (uid) {
        const response = await this.accountApiService.ensureProjection({user_id: uid});
        if (response.code !== 0) {
          throw new Error(response.message || 'ensure projection failed');
        }
        await this.storageService.lfSet('userInfo', response.result);
        this.username.set(response.result?.username ?? this.username());
      }

      this.nzMessageService.success(
        this.translocoService.translate('shell.header.switch_region_kept_session', {
          region: this.regionName(this.region()),
        }),
      );
      // regionEpoch reloads list data even when the destination path is unchanged.
      await this.router.navigateByUrl(regionSwitchTarget(this.router, currentUrl, this.region()));
    } catch (error) {
      if (fromRegion) {
        await this.regionService.setRegion(fromRegion);
        this.region.set(this.regionService.regionCode());
      }
      // ensureProjection uses silent HTTP errors; always show one switch-specific toast here.
      const detail = error instanceof Error ? error.message.trim() : '';
      this.nzMessageService.error(
        detail && !detail.startsWith('Http failure')
          ? detail
          : this.translocoService.translate('shell.header.switch_region_failed', {
            region: targetName,
          }),
      );
    } finally {
      this.nzMessageService.remove(loading.messageId);
    }
  }

  protected regionName(code: string): string {
    return this.translocoService.translate(`shared.regions.${code}`);
  }

  async logout() {
    await this.accountService.logout();
  }

}
